import { Callout, Link } from "@fluentui/react";
import { Column } from "@ioip/core/lib";
import { CodeEditor } from "ioip-base/lib/common";
import React, { useRef, useState } from "react";
import { ControlType } from "../../types";
import { IPFieldProps } from '../../types/field-props';
import CCard from "../types/CCard";
import CCheckBox from "../types/CCheckBox";
import CComboBox from "../types/CComboBox";
import CControl from "../types/CControl";
import CDataTable from "../types/CDataTable";
import CDatePicker from "../types/CDatePicker";
import CImage from "../types/CImage";
import CLabel from "../types/CLabel";
import CRow from "../types/CRow";
import CSubtitle from "../types/CSubtitle";
import CTextField from "../types/CTextField";
import JSGroupedDataGrid from "../types/JSGroupedDataGrid";
import { PSearchableComboBox } from "./PSearchableComboBox";
import { PSubtitle } from "./PSubtitle";
import { PCheckBox } from "./UXCheckBox";
import { UXCitySelector } from "./UXCitySelector";
import { UXComboBox } from "./UXComboBox";
import { UXCountrySelector } from "./UXCountrySelector";
import { UXDataTable } from "./UXDataTable";
import { UXDateTime } from "./UXDateTime";
import { UXDepartmentSelector } from "./UXDepartmentSelector";
import { UXGroupedDataGrid } from "./UXGroupedDataGrid";
import { UXHorizontalLine } from "./UXHorizontalLine";
import { UXImage } from "./UXImage";
import { UXLabel } from "./UXLabel";
import { UXNumberField } from "./UXNumberField";
import { UXRow } from "./UXRow";
import { UXTextField } from "./UXTextField";
import { UXTitle } from "./UXTitle";
import { XCard } from "./XCard";

/**
 * Control genérico que renderiza un control de acuerdo al tipo de control
 * @param props 
 * @returns 
 */
export const UXField = (props: IPFieldProps<CControl>): JSX.Element => {

    const ref = useRef(null);
    const [ showErrorDetails, setShowErrorDetails ] = useState(false);

    // Evaluar visibilidad
    const visible = props.field.controlType == ControlType.row
        ? props.field.cast<CRow>().children.any(x => x.visible)
        : props.field.visible;

    if (!visible)
        return <></>;

    const renderControl = () => {

        const controls = {
            [ ControlType.checkBox ]: <PCheckBox  {...props} field={props.field.cast<CCheckBox>()} />,
            [ ControlType.citySelector ]: <UXCitySelector {...props} field={props.field.cast<CComboBox>()} />,
            [ ControlType.comboBox ]: <UXComboBox  {...props} field={props.field.cast<CComboBox>()} />,
            [ ControlType.countrySelector ]: <UXCountrySelector {...props} field={props.field.cast<CComboBox>()} />,
            [ ControlType.dataTable ]: <UXDataTable  {...props} field={props.field.cast<CDataTable>()} />,
            [ ControlType.datePicker ]: <UXDateTime  {...props} field={props.field.cast<CDatePicker>()} />,
            [ ControlType.dateTimePicker ]: <UXDateTime  {...props} field={props.field.cast<CDatePicker>()} />,
            [ ControlType.departmentSelector ]: <UXDepartmentSelector {...props} field={props.field.cast<CComboBox>()} />,
            [ ControlType.documentUploader ]: <></>,
            [ ControlType.entityDataTable ]: <UXDataTable  {...props} field={props.field.cast<CDataTable>()} />,
            [ ControlType.groupedDataGrid ]: <UXGroupedDataGrid {...props} field={props.field.cast<JSGroupedDataGrid>()} />,
            [ ControlType.horizontalLine ]: <UXHorizontalLine  {...props} />,
            [ ControlType.image ]: <UXImage {...props} field={props.field.cast<CImage>()} />,
            [ ControlType.label ]: <UXLabel  {...props} field={props.field.cast<CLabel>()} />,
            [ ControlType.numericTextField ]: <UXNumberField {...props} field={props.field.cast<CTextField>()} />,
            [ ControlType.radicadoEntradaViewer ]: props.customComponents?.RadicadoEntradaViewer?.({ ...props }) ?? <span>Not supported</span>,
            [ ControlType.row ]: <UXRow  {...props} field={props.field.cast<CRow>()} />,
            [ ControlType.searchableComboBox ]: <PSearchableComboBox  {...props} />,
            [ ControlType.subtitle ]: <PSubtitle  {...props} field={props.field.cast<CSubtitle>()} />,
            [ ControlType.textField ]: <UXTextField {...props} field={props.field.cast<CTextField>()} />,
            [ ControlType.title ]: <UXTitle  {...props} field={props.field.cast<CLabel>()} />,
            [ ControlType.card ]: <XCard  {...props} field={props.field.cast<CCard>()} />
        }

        const controlType = props.field.controlType;
        return controls[ controlType ] || <>
            <Link>Unknown control<small ref={ref} onClick={() => setShowErrorDetails(true)}> (detalle)</small></Link>
            {showErrorDetails && <Callout
                onDismiss={() => setShowErrorDetails(false)}
                target={ref}>
                <div style={{ width: '1000px' }}>
                    <CodeEditor
                        language="json"
                        value={JSON.stringify({ controlType: ControlType[ props.field.controlType ], id: props.field.id }, null, 4)}
                        onChanged={() => { }}
                        height='500px'
                    />
                </div>
            </Callout>}
        </>;

    }

    const colSm = props.field.colSm || 12;
    const colMd = props.field.colMd || 12;
    const colLg = props.field.colLg || 12;
    const colXl = props.field.colXl || 12;
    const colXxl = props.field.colXxl || 12;
    const colXxxl = props.field.colXxxl || 12;

    return props.field.controlType == ControlType.row
        ? renderControl()
        : <Column sm={colSm} md={colMd} lg={colLg} xl={colXl} xxl={colXxl} xxxl={colXxxl}>
            {renderControl()}
        </Column>

}